
export interface PackingItem {
  id: string;
  label: string;
  checked: boolean;
  custom?: boolean;
}

export interface PackingCategory {
  id: string;
  emoji: string;
  label: string;
  items: PackingItem[];
}

export interface PackingList {
  id: string;
  name: string;
  categories: PackingCategory[];
  createdAt: number;
  updatedAt: number;
  archived: boolean;
  folderId?: string | null;
}

type TFn = (key: string) => string;

/** Category/item ID structure (static, for merging & collapse logic) */
export const CATEGORY_IDS = ['sleep', 'food', 'clothes', 'gear', 'safety'] as const;

const CATEGORY_META: { id: string; emoji: string; labelKey: string; items: { id: string; labelKey: string }[] }[] = [
  {
    id: 'sleep', emoji: '🏕️', labelKey: 'packing.categories.sleep',
    items: [
      { id: 'tent',   labelKey: 'packing.items.tent' },
      { id: 'bag',    labelKey: 'packing.items.bag' },
      { id: 'mat',    labelKey: 'packing.items.mat' },
      { id: 'pillow', labelKey: 'packing.items.pillow' },
      { id: 'tarp',   labelKey: 'packing.items.tarp' },
    ],
  },
  {
    id: 'food', emoji: '🍳', labelKey: 'packing.categories.food',
    items: [
      { id: 'stove',   labelKey: 'packing.items.stove' },
      { id: 'pot',     labelKey: 'packing.items.pot' },
      { id: 'cutlery', labelKey: 'packing.items.cutlery' },
      { id: 'bottle',  labelKey: 'packing.items.bottle' },
      { id: 'filter',  labelKey: 'packing.items.filter' },
      { id: 'food',    labelKey: 'packing.items.food' },
      { id: 'coffee',  labelKey: 'packing.items.coffee' },
    ],
  },
  {
    id: 'clothes', emoji: '👕', labelKey: 'packing.categories.clothes',
    items: [
      { id: 'rain',   labelKey: 'packing.items.rain' },
      { id: 'fleece', labelKey: 'packing.items.fleece' },
      { id: 'base',   labelKey: 'packing.items.base' },
      { id: 'socks',  labelKey: 'packing.items.socks' },
      { id: 'boots',  labelKey: 'packing.items.boots' },
      { id: 'hat',    labelKey: 'packing.items.hat' },
      { id: 'sun',    labelKey: 'packing.items.sun' },
    ],
  },
  {
    id: 'gear', emoji: '🔦', labelKey: 'packing.categories.gear',
    items: [
      { id: 'light', labelKey: 'packing.items.light' },
      { id: 'map',   labelKey: 'packing.items.map' },
      { id: 'knife', labelKey: 'packing.items.knife' },
      { id: 'fire',  labelKey: 'packing.items.fire' },
      { id: 'rope',  labelKey: 'packing.items.rope' },
      { id: 'tape',  labelKey: 'packing.items.tape' },
      { id: 'bag2',  labelKey: 'packing.items.bag2' },
    ],
  },
  {
    id: 'safety', emoji: '🚑', labelKey: 'packing.categories.safety',
    items: [
      { id: 'first',   labelKey: 'packing.items.first' },
      { id: 'phone',   labelKey: 'packing.items.phone' },
      { id: 'whistle', labelKey: 'packing.items.whistle' },
      { id: 'id',      labelKey: 'packing.items.id' },
      { id: 'bug',     labelKey: 'packing.items.bug' },
      { id: 'meds',    labelKey: 'packing.items.meds' },
    ],
  },
];

/** Build localized default categories. Pass `t` from useTranslation(). */
export function getDefaultCategories(t: TFn): PackingCategory[] {
  return CATEGORY_META.map(cat => ({
    id: cat.id,
    emoji: cat.emoji,
    label: t(cat.labelKey),
    items: cat.items.map(item => ({
      id: item.id,
      label: t(item.labelKey),
      checked: false,
    })),
  }));
}

/** Static DEFAULT_CATEGORIES kept for backwards-compat (collapse logic, ID lists).
 *  Labels here are Norwegian fallback — always prefer getDefaultCategories(t) for display. */
export const DEFAULT_CATEGORIES: PackingCategory[] = CATEGORY_META.map(cat => ({
  id: cat.id,
  emoji: cat.emoji,
  label: cat.labelKey,
  items: cat.items.map(item => ({
    id: item.id,
    label: item.labelKey,
    checked: false,
  })),
}));
